/**
 * @file Base hook behind every Signify callable wrapper in this directory. It owns the
 * `data`/`loading`/`error` trio that `useSignifyConfigurationSidebar` and the fetch-job
 * hooks render, and each wrapper returns `execute` straight through.
 * The invariant: on failure `execute` records the message in `error` and then rethrows the
 * *original* error, never a copy, so an `HttpsError`'s `code` survives to the caller.
 * Callers must not read `error` to build a throw — that state is a render behind.
 */

import { useState, useCallback } from 'react';

export const useSignifyApi = <T = any>() => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Run an API call, mirroring its outcome into loading/data/error state.
   * @param apiCall - Function that performs the API call
   * @returns The API response; rejects with the original error when the call fails
   */
  const execute = useCallback(async <R extends T = T>(apiCall: () => Promise<R>): Promise<R> => {
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await apiCall();
      setData(response);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'API call failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear all state
   */
  const clear = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  /**
   * Clear only error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    clear,
    clearError,
    setData,
    setError,
    setLoading,
  };
};
